import type { AdminAssessmentListItem } from '@nestjs-boilerplate/types';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { type DataCardConfig, DataCardList } from '@/components/ui/data-card-list';
import { DataTable, sortableHeader } from '@/components/ui/data-table';
import { DateFilterInput } from '@/components/ui/date-filter-input';
import { Label } from '@/components/ui/label';
import { MobileSortControl, type MobileSortOption } from '@/components/ui/mobile-sort-control';
import { MultiSelect } from '@/components/ui/multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/features/dashboard/components/pagination';
import { SearchInput } from '@/features/dashboard/components/search-input';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, type PaginationSearch } from '@/lib/pagination-search';
import { type AdminAssessmentFilters, useAdminAssessments } from '../api/assessments';

/**
 * Risk levels the filter offers.
 *
 * No "Any" entry: an empty selection is the no-filter state, so clearing the
 * control is how a reader widens the list again.
 */
const RISK_LEVELS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

/**
 * Selecting nothing means "everything except deleted", which is what the
 * endpoint does when no status is sent and what the overview count already
 * reports — so the placeholder on the control says exactly that. Deleted is
 * offered explicitly rather than hidden: a user deleting an assessment is
 * precisely what an administrator might be looking for.
 */
const STATUSES = [
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'DELETED', label: 'Deleted' },
];

/**
 * What the server orders by when the request names no column.
 *
 * Held here so the Created header shows the ordering that is actually in
 * effect. It is deliberately not sent: an explicit `sortBy=createdAt` would
 * make the URL claim a choice the reader never made, and the server's unsorted
 * order is not quite the same thing — its tiebreaker runs descending too.
 */
const DEFAULT_SORTING: SortingState = [{ id: 'createdAt', desc: true }];

/**
 * Sort options offered on mobile.
 *
 * The same three columns the table exposes a sortable header for — the ids are
 * the API's own sort columns and are sent as-is, so anything else here would be
 * a control that appears to work and returns a 400.
 *
 * Risk and status are Postgres enums, so the server orders them by declaration
 * order rather than alphabetically: LOW→MEDIUM→HIGH and
 * IN_PROGRESS→COMPLETED→DELETED. The direction labels name what descending
 * actually puts on top, which "Z → A" would get wrong for both.
 */
const MOBILE_SORT_OPTIONS: MobileSortOption[] = [
  { id: 'createdAt', label: 'Created', descLabel: 'Newest first', ascLabel: 'Oldest first' },
  { id: 'riskLevel', label: 'Risk', descLabel: 'Highest first', ascLabel: 'Lowest first' },
  { id: 'status', label: 'Status', descLabel: 'Deleted first', ascLabel: 'In progress first' },
];

const RISK_CLASSES: Record<string, string> = {
  HIGH: 'text-destructive font-medium',
  MEDIUM: 'font-medium',
  LOW: 'text-muted-foreground',
};

/**
 * Placeholder rows shown while a page is fetching.
 *
 * Rendered into DataTable's empty slot, which is a single spanning cell, so
 * the bars are stacked in one cell rather than being five table rows. The
 * space-y-8 reproduces the 1rem top and bottom cell padding that separated
 * the real rows.
 */
const LOADING_ROWS = (
  <div className="space-y-8">
    {['a', 'b', 'c', 'd', 'e'].map((key) => (
      <Skeleton key={key} className="h-5 w-full" />
    ))}
  </div>
);

/**
 * The reference as a link to the assessment.
 *
 * Kept as a real anchor alongside the row's click handler rather than replaced
 * by it. The handler gives the whole row as a target; the anchor is what keeps
 * middle-click and open-in-new-tab working, which a click handler cannot
 * serve, and gives screen readers a named link within the row.
 */
function AssessmentRefLink({ item }: { item: AdminAssessmentListItem }) {
  return (
    <Link
      to="/admin/assessments/$id"
      params={{ id: item.id }}
      className="hover:underline"
      // The row is clickable too. Without this the anchor navigates and then
      // the row handler navigates again to the same place, pushing a
      // duplicate history entry that makes Back appear not to work.
      onClick={(event) => event.stopPropagation()}
    >
      {item.optimalKycRef}
    </Link>
  );
}

/**
 * Risk level, with its score in brackets when the assessment has one.
 *
 * Shared by the table cell and the card's trailing slot so the colouring and
 * the unscored case are decided once.
 */
function RiskValue({ item }: { item: AdminAssessmentListItem }) {
  return (
    <div className="whitespace-nowrap text-sm">
      <span className={RISK_CLASSES[item.riskLevel] ?? ''}>{item.riskLevel}</span>
      {item.riskScore !== null ? (
        <span className="text-xs text-muted-foreground"> ({item.riskScore})</span>
      ) : null}
    </div>
  );
}

/**
 * The client name, or an explicit "purged" where the retention policy has
 * emptied it. A blank would read as data that never existed rather than data
 * deliberately removed.
 */
function ClientName({ item }: { item: AdminAssessmentListItem }) {
  return item.purged ? (
    <span className="text-muted-foreground italic">purged</span>
  ) : (
    item.clientName
  );
}

interface AssessmentsPageProps {
  filters: AdminAssessmentFilters & PaginationSearch;
  onFiltersChange: (next: AdminAssessmentFilters & PaginationSearch) => void;
}

/**
 * Assessments across every user, newest first.
 *
 * Filter state is owned by the route and passed in, so this component needs no
 * router to be tested and the URL stays the single source of truth — a filtered
 * list survives a drill-down into an assessment and back, which it did not while
 * the filters lived in component state.
 */
export function AssessmentsPage({ filters, onFiltersChange }: AssessmentsPageProps) {
  const isMobile = useIsMobile();
  // Paging travels in the same search params as the filters, but the query hook
  // takes it as its own two arguments. Split back out here rather than left in
  // the filter object, which would put the page number in the query key twice.
  const { page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE, ...listFilters } = filters;
  const { data, isLoading, isError, error } = useAdminAssessments(listFilters, page, pageSize);
  const navigate = useNavigate();

  const update = useCallback(
    (patch: Partial<AdminAssessmentFilters & PaginationSearch>) => {
      // Any change to the filter set invalidates the current page number: page 4
      // of the old result set is very likely past the end of the new one. The
      // reset rides along in the one write rather than being a second
      // navigation, which would make one Back press appear to do nothing.
      const resetsPage = !('page' in patch);
      onFiltersChange({ ...filters, ...patch, ...(resetsPage ? { page: undefined } : {}) });
    },
    [filters, onFiltersChange],
  );

  // Falls back to the server's own default rather than to no sort at all, so
  // the Created header does not show as unsorted while the rows are in fact
  // ordered by it.
  const sorting: SortingState = filters.sortBy
    ? [{ id: filters.sortBy, desc: filters.sortOrder !== 'asc' }]
    : DEFAULT_SORTING;

  const onSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      update(
        first
          ? {
              // Cast because TanStack reports a column id as a bare string. The
              // ids are declared below and are the API's own sort columns, and
              // the route's `validateSearch` narrows this again on the way back
              // in.
              sortBy: first.id as AdminAssessmentFilters['sortBy'],
              sortOrder: first.desc ? 'desc' : 'asc',
            }
          : { sortBy: undefined, sortOrder: undefined },
      );
    },
    [sorting, update],
  );

  // Whole-row navigation. The reference stays a real <Link> as well: the row
  // handler cannot serve middle-click or open-in-new-tab, so removing the
  // anchor would take those away to gain a larger target. Keeping both means
  // the row is easy to hit and the link still behaves like a link.
  const openAssessment = useCallback(
    (row: AdminAssessmentListItem) => {
      navigate({ to: '/admin/assessments/$id', params: { id: row.id } });
    },
    [navigate],
  );

  const items = data?.items ?? [];

  const columns = useMemo<ColumnDef<AdminAssessmentListItem>[]>(
    () => [
      {
        id: 'reference',
        header: 'Reference',
        // Reference, Client and Owner have no server-side ordering behind them
        // — the first two are not on the allowlist and the third sorts on a
        // joined row — and a header that looks like a control but reorders
        // nothing is worse than a plain one.
        enableSorting: false,
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-sm font-medium">
            <AssessmentRefLink item={row.original} />
            <div className="text-xs text-muted-foreground">{row.original.clientRef}</div>
          </div>
        ),
      },
      {
        id: 'client',
        header: 'Client',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-sm">
            <ClientName item={row.original} />
          </div>
        ),
      },
      {
        id: 'owner',
        header: 'Owner',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-sm">
            <div>{row.original.ownerEmail}</div>
            {row.original.ownerCompany ? (
              <div className="text-xs text-muted-foreground">{row.original.ownerCompany}</div>
            ) : null}
          </div>
        ),
      },
      {
        // Named for the API's sort column, not the label: TanStack reports the
        // column id as the sort key and it is sent to the server as-is. The
        // cell shows the score alongside, but the ordering is by level — the
        // two agree on which rows matter, and one header over one cell cannot
        // offer both.
        id: 'riskLevel',
        header: sortableHeader<AdminAssessmentListItem>('Risk'),
        cell: ({ row }) => <RiskValue item={row.original} />,
      },
      {
        id: 'status',
        header: sortableHeader<AdminAssessmentListItem>('Status'),
        cell: ({ row }) => <div className="text-sm">{row.original.status}</div>,
      },
      {
        id: 'createdAt',
        header: sortableHeader<AdminAssessmentListItem>('Created'),
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-sm text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </div>
        ),
      },
    ],
    [],
  );

  // Triage on a phone is "which of these needs looking at?", so risk takes the
  // trailing slot — top right, read before anything below it — and the two
  // references become the card's identity. Client, owner, status and date are
  // the detail you read once the risk has already earned your attention.
  const cardConfig = useMemo<DataCardConfig<AdminAssessmentListItem>>(
    () => ({
      header: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold">
            <AssessmentRefLink item={row} />
          </span>
          <span className="text-sm text-muted-foreground">{row.clientRef}</span>
        </div>
      ),
      trailing: (row) => <RiskValue item={row} />,
      fields: [
        { label: 'Client', render: (row) => <ClientName item={row} /> },
        {
          label: 'Owner',
          render: (row) => (
            <div>
              <div className="break-all">{row.ownerEmail}</div>
              {row.ownerCompany ? (
                <div className="text-xs text-muted-foreground">{row.ownerCompany}</div>
              ) : null}
            </div>
          ),
        },
        { label: 'Status', render: (row) => row.status },
        {
          label: 'Created',
          render: (row) => new Date(row.createdAt).toLocaleDateString(),
        },
      ],
      onClick: openAssessment,
    }),
    [openAssessment],
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Assessment Records</h1>
      <Card>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="search">Search</Label>
            {/* Debounced, so a typed word is one request and one URL write
                rather than one of each per keystroke. */}
            <SearchInput
              id="search"
              placeholder="OKYC ref, client ref or name"
              value={filters.search ?? ''}
              onChange={(search) => update({ search: search || undefined })}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="owner">Owner</Label>
            <SearchInput
              id="owner"
              placeholder="Name or email"
              value={filters.owner ?? ''}
              onChange={(owner) => update({ owner: owner || undefined })}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="riskLevel">Risk level</Label>
            <MultiSelect
              id="riskLevel"
              value={filters.riskLevel ?? []}
              onChange={(value) =>
                update({
                  riskLevel:
                    value.length > 0 ? (value as AdminAssessmentFilters['riskLevel']) : undefined,
                })
              }
              options={RISK_LEVELS}
              placeholder="Any risk level"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <MultiSelect
              id="status"
              value={filters.status ?? []}
              onChange={(value) =>
                update({
                  status:
                    value.length > 0 ? (value as AdminAssessmentFilters['status']) : undefined,
                })
              }
              options={STATUSES}
              placeholder="Active (excludes deleted)"
            />
          </div>
          {/* Both ends inclusive. The pair is cross-bounded, so the calendar
              cannot offer a range that reads backwards and returns nothing. */}
          <div className="space-y-2">
            <Label htmlFor="from">From</Label>
            <DateFilterInput
              id="from"
              value={filters.from}
              onChange={(from) => update({ from })}
              max={filters.to ? new Date(filters.to) : undefined}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="to">To</Label>
            <DateFilterInput
              id="to"
              value={filters.to}
              onChange={(to) => update({ to })}
              min={filters.from ? new Date(filters.from) : undefined}
            />
          </div>
        </CardContent>
      </Card>

      {isError ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Could not load assessments
              {error instanceof Error ? `: ${error.message}` : '.'}
            </p>
          </CardContent>
        </Card>
      ) : isMobile ? (
        // No outer Card on mobile: the cards are already separate surfaces, so
        // wrapping them in one more would nest a card in a card.
        <div className="space-y-4">
          <MobileSortControl
            options={MOBILE_SORT_OPTIONS}
            value={sorting[0] ? { id: sorting[0].id, desc: sorting[0].desc } : undefined}
            onChange={(next) => {
              onSortingChange([{ id: next.id, desc: next.desc }]);
            }}
          />
          {isLoading ? (
            // Card-shaped placeholders, not LOADING_ROWS: that one's spacing is
            // tuned to table cell padding and reads as thin bars here.
            <div data-testid="assessments-loading" className="space-y-3">
              {['a', 'b', 'c'].map((key) => (
                <Skeleton key={key} className="h-32 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <DataCardList<AdminAssessmentListItem>
              data={items}
              getRowId={(row) => row.id}
              cardConfig={cardConfig}
              emptyMessage="No assessments match these filters."
            />
          )}

          {items.length > 0 ? (
            <Pagination
              page={data?.page ?? 1}
              pageSize={data?.pageSize ?? pageSize}
              total={data?.total ?? 0}
              totalPages={data?.totalPages ?? 0}
              onPageChange={(next) => update({ page: next })}
              onPageSizeChange={(next) => update({ pageSize: next, page: undefined })}
            />
          ) : null}
        </div>
      ) : (
        <Card>
          {/* Loading and empty both render through DataTable's empty slot, so
              the headers always come from the column definitions and there is
              one table rendering to maintain rather than two. */}
          <DataTable
            columns={columns}
            data={isLoading ? [] : items}
            getRowId={(row) => row.id}
            sorting={sorting}
            onSortingChange={onSortingChange}
            onRowClick={openAssessment}
            emptyMessage={
              isLoading ? (
                LOADING_ROWS
              ) : (
                <div className="text-center text-muted-foreground py-4">
                  No assessments match these filters.
                </div>
              )
            }
            unframed
          />

          {items.length > 0 ? (
            <div className="border-t p-4">
              <Pagination
                page={data?.page ?? 1}
                pageSize={data?.pageSize ?? pageSize}
                total={data?.total ?? 0}
                totalPages={data?.totalPages ?? 0}
                onPageChange={(next) => update({ page: next })}
                onPageSizeChange={(next) => update({ pageSize: next, page: undefined })}
              />
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}
